// src/components/GalileoPet.js
// React wrapper for the Galileo Web Pet mascot.
// The pet logic is self-contained and is injected into the DOM via useEffect.
// Assets live in /public/mascot/ and are referenced via process.env.PUBLIC_URL.

import { useEffect } from 'react';
import './GalileoPet.css';

const ZOOM = 0.9;

class GalileoPet {
    constructor(basePath = '/mascot/') {
        this.basePath = basePath;
        this.width = 120;
        this.height = 120;
        this.x = (window.innerWidth / ZOOM) - this.width - 24;   // 24px margin from right edge
        this.y = -this.height;                           // start above the viewport

        this.vx = 0;
        this.vy = 0;
        this.gravity = 0.3;
        this.speed = 2;

        this.state = 'FALLING'; // drop in from top on load
        this.action = 'jumping';
        this.forcedActionTimer = 0;

        this.isDragging = false;
        this.isCollapsed = false;
        this.isHovered = false;
        this.dragOffsetX = 0;
        this.dragOffsetY = 0;

        this.wanderTimer = 0;
        this.wanderDir = Math.random() > 0.5 ? 1 : -1;

        this._destroyed = false;

        this.initDOM();
        this.bindEvents();

        this._rafId = requestAnimationFrame(() => this.loop());
        this._speechInterval = setInterval(() => this.speechLoop(), 5000);
    }

    initDOM() {
        this.container = document.createElement('div');
        this.container.id = 'galileo-pet-container';

        this.bubble = document.createElement('div');
        this.bubble.id = 'galileo-bubble';

        this.sprite = document.createElement('div');
        this.sprite.id = 'galileo-pet-sprite';
        this.sprite.title = 'Double-click to collapse/expand!';

        this.container.appendChild(this.bubble);
        this.container.appendChild(this.sprite);
        document.body.appendChild(this.container);

        this.updateAction('failed');
        this.updatePosition();
    }

    bindEvents() {
        this._keyBuffer = '';
        this._globalReactionHandler = (e) => {
            const { type, message } = e.detail || {};
            if (type === 'success') {
                this.forceAction('jumping', message || 'Yay! It worked!');
            } else if (type === 'error') {
                this.forceAction('failed', message || 'Oops! Something went wrong.');
            }
        };

        this._themeChangeHandler = (e) => {
            const isDark = e.detail?.isDark;
            this.forceAction(
                isDark ? 'waiting' : 'review',
                isDark ? 'Bravo Six, going dark.' : 'Whoa, so bright!'
            );
        };

        this._onKeyDown = (e) => {
            if (e.key === 'Shift') {
                this.isShiftDown = true;
            }
            if (e.key && e.key.length === 1) {
                this._keyBuffer += e.key.toLowerCase();
                if (this._keyBuffer.length > 15) {
                    this._keyBuffer = this._keyBuffer.slice(-15);
                }

                if (this._keyBuffer.includes('dog') || this._keyBuffer.includes('galileo') || this._keyBuffer.includes('fetch') || this._keyBuffer.includes('hola') || this._keyBuffer.includes('umbrella')) {
                    this.forceAction('jumping', 'Did someone say my name?!');
                    this._keyBuffer = ''; // clear buffer
                } else if (this._keyBuffer.includes('umbrella')) {
                    this.forceAction('waving', 'UmbrellaCo! That is where I live!');
                    this._keyBuffer = '';
                }
            }
        };

        this._onKeyUp = (e) => {
            if (e.key === 'Shift') {
                this.isShiftDown = false;
            }
        };

        this._onMouseDown = (e) => {
            if (this.isCollapsed) return;
            this.isDragging = true;
            this.state = 'DRAGGING';
            this.updateAction('failed');
            this.dragOffsetX = e.clientX - this.x;
            this.dragOffsetY = e.clientY - this.y;
            this.container.style.transition = 'none';
        };

        this._onMouseMove = (e) => {
            this.mouseX = e.clientX / ZOOM;
            this.mouseY = e.clientY / ZOOM;
            if (this.isDragging) {
                this.x = e.clientX - this.dragOffsetX;
                this.y = e.clientY - this.dragOffsetY;
                this.updatePosition();
            }
        };

        this._onMouseUp = () => {
            if (this.isDragging) {
                this.isDragging = false;
                this.state = 'FALLING';
                this.container.style.transition =
                    'transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
            }
        };

        this._onDblClick = () => {
            this.isCollapsed = !this.isCollapsed;
            if (this.isCollapsed) {
                this.container.classList.add('galileo-collapsed');
                this.updateAction('idle');
            } else {
                this.container.classList.remove('galileo-collapsed');
            }
        };

        // Hover — stop and wave
        this._onMouseEnter = () => {
            if (this.isCollapsed || this.isDragging) return;
            this.isHovered = true;
            this.updateAction('waving');
        };

        this._onMouseLeave = () => {
            this.isHovered = false;
            // Resume from a sitting pause so the transition feels natural
            this.state = 'SITTING';
            this.wanderTimer = 60;
        };

        this.sprite.addEventListener('mousedown', this._onMouseDown);
        window.addEventListener('mousemove', this._onMouseMove);
        window.addEventListener('mouseup', this._onMouseUp);
        window.addEventListener('keydown', this._onKeyDown);
        window.addEventListener('keyup', this._onKeyUp);
        window.addEventListener('pet:reaction', this._globalReactionHandler);
        window.addEventListener('pet:theme_change', this._themeChangeHandler);
        this.sprite.addEventListener('dblclick', this._onDblClick);
        this.sprite.addEventListener('mouseenter', this._onMouseEnter);
        this.sprite.addEventListener('mouseleave', this._onMouseLeave);
    }

    forceAction(actionName, speechText) {
        if (this.isCollapsed) return;
        this.updateAction(actionName);
        this.state = 'SITTING';
        this.wanderTimer = 200; // sit for a while
        this.forcedActionTimer = 180; // hold action for 3 seconds
        this.targetX = null;

        if (speechText) {
            this.bubble.innerText = speechText;
            this.bubble.classList.add('show');
            setTimeout(() => {
                this.bubble.classList.remove('show');
            }, 4000);
        }
    }

    updateAction(newAction) {
        if (this.action !== newAction) {
            this.action = newAction;
            this.sprite.style.backgroundImage = `url('${this.basePath}${newAction}.webp')`;
        }
    }

    updatePosition() {
        this.container.style.left = `${this.x}px`;
        this.container.style.top = `${this.y}px`;
    }

    getTargets() {
        const targets = Array.from(document.querySelectorAll('.pet-target'));
        return targets.map((t) => {
            const rect = t.getBoundingClientRect();
            return {
                left: rect.left / ZOOM,
                right: rect.right / ZOOM,
                top: rect.top / ZOOM,
                bottom: rect.bottom / ZOOM,
            };
        });
    }

    getInterestingElements() {
        // Find visible interactive/interesting elements to walk towards
        const selectors = 'button, input, a, h1, h2, h3, .pet-target, .MuiButton-root';
        const elements = Array.from(document.querySelectorAll(selectors)).filter(el => {
            const rect = el.getBoundingClientRect();
            // Must have size and be somewhat visible on the Y axis
            return rect.width > 0 && rect.height > 0 && rect.top < window.innerHeight && rect.bottom > 0;
        });

        return elements.map(el => {
            const rect = el.getBoundingClientRect();
            return {
                x: (rect.left + rect.width / 2) / ZOOM, // horizontal center
                y: rect.top / ZOOM,
                tag: el.tagName.toLowerCase(),
                placeholder: el.placeholder || '',
                text: el.innerText || ''
            };
        });
    }

    loop() {
        if (this._destroyed) return;
        if (!this.isDragging && !this.isCollapsed && !this.isHovered) {
            this.updatePhysics();
        }
        this.updatePosition();
        this._rafId = requestAnimationFrame(() => this.loop());
    }

    updatePhysics() {
        if (this.isShiftDown && this.mouseX !== undefined && this.mouseY !== undefined && !this.isCollapsed) {
            // Chase the cursor anywhere (flying mode)
            this.state = 'FETCHING';
            this.vy = 0; // Suspend gravity

            const dx = this.mouseX - (this.x + this.width / 2);
            const dy = this.mouseY - (this.y + this.height / 2);

            // Move on X and Y
            if (Math.abs(dx) > 5) this.x += Math.sign(dx) * this.speed * 1.5; // Run slightly faster
            if (Math.abs(dy) > 5) this.y += Math.sign(dy) * this.speed * 1.5;

            // Update direction for sprite
            this.wanderDir = dx > 0 ? 1 : -1;

            // If we are close to the cursor, maybe do a little jump
            if (Math.abs(dx) < 15 && Math.abs(dy) < 15) {
                this.updateAction('jumping');
            } else {
                this.updateAction(this.wanderDir > 0 ? 'running-right' : 'running-left');
            }
            return; // skip normal gravity and wandering
        }

        let floorY = window.innerHeight / ZOOM;
        let bounds = { left: 0, right: window.innerWidth / ZOOM };

        const petCenterX = this.x + this.width / 2;
        const targets = this.getTargets();

        let onTarget = false;
        for (let t of targets) {
            if (petCenterX >= t.left && petCenterX <= t.right) {
                if (this.y + this.height <= t.top + 15) {
                    floorY = Math.min(floorY, t.top);
                    bounds.left = Math.max(bounds.left, t.left);
                    bounds.right = Math.min(bounds.right, t.right);
                    onTarget = true;
                }
            }
        }

        if (this.y + this.height < floorY - 5) {
            this.state = 'FALLING';
            this.vy += this.gravity;
            this.y += this.vy;
            this.updateAction('jumping'); // use jumping sprite while airborne
        } else {
            // Snap to floor
            this.y = floorY - this.height;

            // Using a threshold of 3.0 and a bounce factor of 0.6
            if (Math.abs(this.vy) > 3.0) {
                // Bounce! Reverse velocity with energy loss
                this.vy = -this.vy * 0.6;
                this.state = 'FALLING'; // stay in falling/bouncing state
                this.updateAction('jumping');

                // CRITICAL: move it slightly off the floor so the next frame detects it as falling!
                this.y += this.vy;
            } else {
                // Settled — stop bouncing
                this.vy = 0;
                if (this.state === 'FALLING') {
                    this.state = 'SITTING';
                    this.wanderTimer = 100;
                }
                this.handleWandering(bounds);
            }
        }
    }

    handleWandering(bounds) {
        this.wanderTimer--;

        if (this.wanderTimer <= 0) {
            if (this.state === 'SITTING') {
                this.state = 'WANDERING';
                this.wanderTimer = Math.random() * 200 + 100;

                // 50% chance to target an interesting element on the screen instead of random
                const interesting = this.getInterestingElements();
                if (interesting.length > 0 && Math.random() > 0.5) {
                    this._currentTarget = interesting[Math.floor(Math.random() * interesting.length)];
                    this.targetX = this._currentTarget.x;
                    this.wanderDir = this.targetX > (this.x + this.width / 2) ? 1 : -1;
                } else {
                    this.targetX = null;
                    this._currentTarget = null;
                    this.wanderDir = Math.random() > 0.5 ? 1 : -1;
                }
            } else {
                this.state = 'SITTING';
                this.wanderTimer = Math.random() * 300 + 100;
                this.targetX = null;
                this._currentTarget = null;
            }
        }

        if (this.state === 'WANDERING') {
            this.x += this.speed * this.wanderDir;

            if (this.x < bounds.left) {
                this.x = bounds.left;
                this.wanderDir = 1;
                this.targetX = null; // give up on target if we hit a wall
            } else if (this.x + this.width > bounds.right) {
                this.x = bounds.right - this.width;
                this.wanderDir = -1;
                this.targetX = null;
            }

            // If we have a target and we reached it
            if (this.targetX !== null) {
                const petCenterX = this.x + this.width / 2;
                if (Math.abs(petCenterX - this.targetX) < 15) {
                    // Reached element! Stop and inspect
                    let customPhrase = null;
                    if (this._currentTarget) {
                        const { tag, placeholder, text } = this._currentTarget;
                        if (tag === 'input' && placeholder.toLowerCase().includes('search')) {
                            customPhrase = "Looking for something?";
                        } else if (tag === 'button') {
                            customPhrase = Math.random() > 0.5 ? "Should I click it?" : "Don't click that!";
                        } else if (tag === 'h1' || tag === 'h2' || tag === 'h3') {
                            customPhrase = `Hmm... "${text.substring(0, 15)}..." Interesting.`;
                        }
                    }

                    this.state = 'SITTING';
                    this.wanderTimer = Math.random() * 200 + 100;
                    this.targetX = null;
                    this._currentTarget = null;

                    if (customPhrase) {
                        this.forceAction(Math.random() > 0.5 ? 'review' : 'waiting', customPhrase);
                    } else {
                        this.forceAction(Math.random() > 0.5 ? 'review' : 'waiting');
                    }
                    return; // skip the running update below
                }
            }

            this.updateAction(this.wanderDir > 0 ? 'running-right' : 'running-left');
        } else {
            if (this.forcedActionTimer > 0) {
                this.forcedActionTimer--;
                return; // preserve forced action
            }

            // If sitting, maintain special inspection actions, otherwise just idle
            if (this.action !== 'review' && this.action !== 'waiting') {
                this.updateAction('idle');
            }
        }
    }

    speechLoop() {
        if (this.isCollapsed || this.state === 'FALLING' || this.isDragging) return;

        if (Math.random() < 0.3) {
            const path = window.location.pathname.toLowerCase();
            let phrases = [
                // General / meta
                '👀 Did you know you can drag me?',
                'Double-click me to hide. I dare you.',
                'I\'m basically the whole reason this site is good.',
                'I\'ve walked this page 47 times today.',
                'Still here. Just vibing.',
                'Psst — refresh the page. I respawn.',
                'I have no idea what I\'m doing here. Neither do you. We\'re the same.',
                'What even is a mascot? Asking for myself.',
                'I am not a bug. I am a feature.',
                'If I fall off the screen, do I even exist?',
                'Gravity — rude, honestly.'
            ];

            if (path.includes('json')) {
                phrases.push(
                    '{ "mood": "excellent" }',
                    'Did someone say JSON? My favorite language 🤓',
                    'I once tried to validate myself. The schema rejected me.',
                    'Try the JSON tools — your APIs will thank you.'
                );
            } else if (path.includes('text')) {
                phrases.push(
                    'Regex is just magic with extra steps.',
                    'Base64 me outside, how \'bout that?',
                    'I once encoded myself in Base64. Still me, just longer.'
                );
            } else if (path.includes('api')) {
                phrases.push(
                    'GET /me/a/snack 🍕',
                    'Status 200: I am doing great!',
                    'Status 418: I\'m a teapot 🫖',
                    'The API Tools won\'t bite. Probably.'
                );
            } else if (path.includes('dev')) {
                phrases.push(
                    'console.log("help, I\'m trapped in a mascot")',
                    'A UUID walks into a bar... 550e8400-e29b...',
                    'I generated a UUID once. It was unique. Obviously.'
                );
            } else if (path.includes('spatial')) {
                phrases.push(
                    '📍 I know where you are. (I don\'t.)',
                    'GeoJSON is just JSON that got outdoors more.',
                    'I tried to map myself once. Got lost.'
                );
            } else if (path.includes('color') || path.includes('image')) {
                phrases.push(
                    '#FF6B6B is my favorite color. Don\'t @ me.',
                    'Beauty is in the eye of the hex code.'
                );
            } else if (path.includes('mail')) {
                phrases.push(
                    'Need a fake email? I know a guy. Several guys, actually.',
                    'Inbox zero? Never heard of her.'
                );
            } else if (path.includes('blog')) {
                phrases.push(
                    '📝 Have you read the blog? It\'s pretty good.',
                    'Fun fact: I tried to write a blog post. It was just "woof."'
                );
            } else if (path.includes('home')) {
                phrases.push(
                    'Welcome home! 🏠',
                    'Make yourself comfortable.',
                    'This is my favorite page. Actually, I say that about all of them.'
                );
            }

            this.bubble.innerText = phrases[Math.floor(Math.random() * phrases.length)];
            this.bubble.classList.add('show');

            setTimeout(() => {
                this.bubble.classList.remove('show');
            }, 3000);
        }
    }

    destroy() {
        this._destroyed = true;
        cancelAnimationFrame(this._rafId);
        clearInterval(this._speechInterval);

        this.sprite.removeEventListener('mousedown', this._onMouseDown);
        window.removeEventListener('mousemove', this._onMouseMove);
        window.removeEventListener('mouseup', this._onMouseUp);
        this.sprite.removeEventListener('dblclick', this._onDblClick);
        this.sprite.removeEventListener('mouseenter', this._onMouseEnter);
        this.sprite.removeEventListener('mouseleave', this._onMouseLeave);

        if (this.container && this.container.parentNode) {
            this.container.parentNode.removeChild(this.container);
        }
    }
}

/**
 * GalileoPet React Component
 *
 * Drop this anywhere in your component tree (e.g. inside <App>).
 * The pet walks along any element that has the CSS class `pet-target`.
 *
 * Double-click the sprite to collapse / expand it.
 * Drag it around the screen.
 */
function GalileoPetComponent() {
    useEffect(() => {
        // Use PUBLIC_URL so it works both in dev and production builds
        const basePath = `${process.env.PUBLIC_URL}/mascot/`;
        const pet = new GalileoPet(basePath);

        return () => {
            pet.destroy();
        };
    }, []);

    // Nothing to render — the pet injects its own DOM nodes
    return null;
}

export default GalileoPetComponent;
